import React, {
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useState,
} from "react";

import { get, filter } from "lodash";
import PropTypes from "prop-types";
import { FormattedMessage, intlShape } from "react-intl";

import { Badge } from "@ps-design-system/components/Badge";

import EndPauseModal from "../_v2/features/pause-listing/components/EndPauseModal";
import ListingStatus from "../_v2/features/pause-listing/components/ListingStatus";
import PauseModal from "../_v2/features/pause-listing/components/PauseModal";
import { usePauseModal } from "../_v2/features/pause-listing/context/PauseModalContext";
import ModalManager from "../elements-modal/modal-manager-v2";
import { StatsigContext } from "../elements-statsig-provider/statsig-context";

import { listingTOC, tocByUseType } from "./data/utils";
import hoc from "./hoc";
import DeleteListingModal from "./modals/DeleteListingModal";
import MyListingsShareModal from "./modals/MyListingsShareModal";
import TOCRow from "./TOCRow";
import { isFsvListing } from "./utils/listing-fsv.utils";

const ListingsTOC = (props) => {
  const { listing, toggleModal, space, modal, intl } = props;

  const { openPauseModal, openEndPauseModal } = usePauseModal();

  const { featureGates } = useContext(StatsigContext);

  useEffect(() => {
    const openModalParam = _.get(props, ["location", "query", "openModal"], "");
    if (openModalParam === "true") {
      openEndPauseModal();
    }
  }, []);

  const isEnabled = featureGates.pause_listing ?? false;
  const isDateBasedPricingEnabled = featureGates.date_based_pricing ?? false;

  const listingData = get(listing, "data", {});
  const isFullServiceListing = isFsvListing(listingData);
  const showPricingNewBadge =
    isDateBasedPricingEnabled && !isFullServiceListing;
  const listingStatus = get(listingData, "status", "");
  const listingId = get(listing, "data._id");
  const spaceData = get(space, "data", {});
  const {
    street = "",
    city = "",
    state = "",
    zip = "",
    country = "",
  } = spaceData;
  const tocHash = tocByUseType[get(listingData, "use_type")] || {};

  const tocData = useMemo(() => {
    const toc = tocHash.toc;
    return filter(listingTOC(listingData), (obj) => {
      const key = obj.key;
      return -1 !== toc.indexOf(key);
    }).map((obj) =>
      "pricing" === obj.key && showPricingNewBadge
        ? {
            ...obj,
            header_icon: (
              <Badge
                appearance="purple"
                className="tw-ml-8"
                size="sm"
                data-testid="pricing-new-badge"
              >
                <FormattedMessage
                  id="hosting.date_rate.new_badge"
                  defaultMessage="NEW!"
                />
              </Badge>
            ),
          }
        : obj,
    );
  }, [listingData, showPricingNewBadge, tocHash.toc]);

  const redirectToListingPreview = useCallback(
    (e) => {
      e.preventDefault();
      window.open(`/pages/listings/${listingId}`);
    },
    [listingId],
  );

  const openShareModal = useCallback(
    (e) => {
      e.preventDefault();
      toggleModal("MyListingsShareModal", {
        listing: listingData,
      });
    },
    [toggleModal, listingData],
  );

  const openDeleteModal = useCallback(
    (e) => {
      e.preventDefault();
      toggleModal("DeleteListingModal", {
        listingId: listingId,
        listing: listingData,
      });
    },
    [toggleModal, listingId, listingData],
  );

  const renderPauseButton = () => {
    if (listingStatus === "ACTIVATED") {
      return (
        <button
          onClick={openPauseModal}
          className="btn btn-default btn-outlined-light tw-flex-shrink-0"
          type="button"
          name="pause listing"
          data-testing-id="pause-button"
        >
          <FormattedMessage id="pause.modal.pause" defaultMessage="Pause" />
        </button>
      );
    }
    return null;
  };

  const renderUnPausedButton = () => {
    if (listingStatus === "PAUSED" || listingStatus === "PAUSED_INTERNALLY") {
      return (
        <button
          onClick={openEndPauseModal}
          className="btn btn-default btn-outlined-light tw-flex-shrink-0"
          type="button"
          name="pause listing"
          data-testing-id="pause-button"
        >
          <FormattedMessage
            id="pause.modal.resume"
            defaultMessage="Resume listing"
          />
        </button>
      );
    }
    return null;
  };

  return (
    <div className="ListingsTOC ps-container-lg-margin ps-container-fixed">
      <div className="ps-row flex column">
        <div className="col-xs-12">
          <div className="flex space-between listing-header margin-xl-bottom margin-xl-top">
            <div>
              <div className="listing-title">
                <h2>
                  <FormattedMessage
                    id="hosting.listings_toc.customize_for"
                    defaultMessage="Customize for {type}"
                    values={{
                      type: intl.formatMessage(tocHash.title),
                    }}
                  />
                </h2>
                {isEnabled && (
                  <div className="listing-status">
                    <ListingStatus status={listingStatus} />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap">
                <p className="text-gray-600 margin-md-right">
                  {street}
                  {street ? "," : ""} {city}
                  {city ? "," : ""} {state}
                  {state ? "," : ""} {zip}
                  {zip ? "," : ""} {country}
                </p>
                <p>
                  <a
                    className="underline"
                    href={`/your-space/${get(listing, "data.parentSpaceId")}`}
                  >
                    <FormattedMessage
                      id="hosting.listings_toc.edit_space_link_text"
                      defaultMessage="Edit Space"
                    />
                  </a>
                </p>
              </div>
            </div>
            <div className="listing-buttons">
              {isEnabled && renderPauseButton()}
              {isEnabled && renderUnPausedButton()}
              {listingStatus !== "DRAFT" && listingStatus !== "DEACTIVATED" && (
                <button
                  onClick={openDeleteModal}
                  className="btn btn-default btn-outlined-light margin-md-left tw-flex-shrink-0"
                  type="button"
                  name="open delete modal"
                  data-testing-id="delete-modal-button"
                >
                  <FormattedMessage
                    id="deactivate.modal.deactivate"
                    defaultMessage="Deactivate"
                  />
                </button>
              )}
              <button
                onClick={redirectToListingPreview}
                className="btn btn-default btn-outlined-light margin-md-left tw-flex-shrink-0"
                type="button"
                name="listing preview"
                data-testing-id="preview-button"
              >
                <FormattedMessage
                  id="hosting.listings_toc.button_text_preview"
                  defaultMessage="Preview"
                />
              </button>
              <button
                onClick={openShareModal}
                className="btn btn-default btn-outlined-light margin-md-left tw-flex-shrink-0"
                type="button"
                name="open share modal"
                data-testing-id="share-modal-button"
              >
                <img
                  className="vertically-align-icons"
                  src="/web-hosting/assets/images/share.svg?v=rb"
                  alt={intl.formatMessage({
                    id: "hosting.listings_toc.share_image_alt",
                    defaultMessage: "share",
                  })}
                />
              </button>
            </div>
          </div>
          <div>
            {tocData.map((item) => (
              <TOCRow key={item.key} testingID={item.key} {...item} />
            ))}
          </div>
          <a
            href="/mylistings"
            className="btn btn-default btn-outlined-light back-button"
            data-testing-id="back-button"
          >
            <FormattedMessage
              id="hosting.listings_toc.back_link_text"
              defaultMessage="Back"
            />
          </a>
          <PauseModal />
          <EndPauseModal />
          <ModalManager
            whichModal={get(modal, "modalId", "")}
            toggleModal={toggleModal}
          >
            <DeleteListingModal
              id="DeleteListingModal"
              listingId={get(modal, "modalData.listingId", "")}
              listingData={get(modal, "modalData.listing", {})}
              {...props}
            />
            <MyListingsShareModal
              id="MyListingsShareModal"
              listingData={get(modal, "modalData.listing", {})}
            />
          </ModalManager>
        </div>
      </div>
    </div>
  );
};

ListingsTOC.propTypes = {
  modal: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  space: PropTypes.object.isRequired,
  toggleModal: PropTypes.func.isRequired,
  intl: intlShape,
  router: PropTypes.object,
};

export default hoc(ListingsTOC);
